/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { useMemo } from 'react';
import { Column, ColumnProps } from 'primereact/column';
import { classNames } from 'primereact/utils';
import { ProcessDescription } from '../types/processing-schemas.ts';
import { DataTable } from 'primereact/datatable';
import { useTranslation } from 'react-i18next';

export interface OGCIOTableProps {
  className?: string;
  type: 'inputs' | 'outputs';
  content?: ProcessDescription['inputs'] | ProcessDescription['outputs'];
  loading?: boolean;
  ignore_inputs?: string[];
  extraInputsToIgnore?: string[];
}

export const OGCIOTable: React.FC<OGCIOTableProps> = ({ content, ...props }: OGCIOTableProps) => {
  const { t } = useTranslation();

  const defaultColumns: ColumnProps[] = useMemo(() => {
    const columns = [
      { field: 'name', header: t(['in_out.name', 'name']), style: { width: '20%' }, order: 1 },
      { field: 'title', header: t(['in_out.title', 'name']), style: { width: '20%' }, order: 2 },
      { field: 'type', header: t(['in_out.type', 'data type']), style: { width: '20%' }, sortable: true, order: 2 },
      { field: 'description', header: 'Description', order: 3 }
    ];

    return columns.toSorted((a, b) => a.order - b.order);
  }, [t]);

  const IOArray = useMemo(() => {
    const values = [];
    if (content) {
      Object.entries(content).forEach(([k, v]) => {
        if (!props.extraInputsToIgnore?.find((toIgnore) => k.startsWith(toIgnore))) {
          values.push({ name: k, type: v.schema.type, description: v.description, title: v.title });
        }
      });
    }
    return values;
  }, [content, props.extraInputsToIgnore]);

  return (
    <DataTable
      width={100}
      size="small"
      loading={props.loading}
      scrollable
      emptyMessage={t(['misc.404', 'No available data'])}
      scrollHeight="200px"
      value={[...IOArray]}
      tableStyle={{ width: '100%' }}
      className={classNames(props.className)}
    >
      {defaultColumns.map((c) => (
        <Column className="cursor-pointer" key={c.field} {...c} />
      ))}
    </DataTable>
  );
};
